import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { ACTIVE_STAGES } from "../data/stages";
import { PipelineChart, WeeklyChart } from "../components/Charts";
import CompanyLogo from "../components/CompanyLogo";
import EmptyState from "../components/EmptyState";
import AccessNotice from "../components/AccessNotice";
import { formatDateTime, timeAgo } from "../utils/format";
import { attentionItems } from "../utils/jobs";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function PasteLinkCard() {
  const { openAddJob } = useApp();
  const [url, setUrl] = useState("");

  return (
    <div className="card paste-card mb-4">
      <div className="card-body p-4">
        <h2 className="h5 mb-1">Found a job you like?</h2>
        <p className="text-muted small mb-3">
          Paste the link. We'll detect the details, then help you tailor your resume, write a cover letter, apply and
          reach the recruiter.
        </p>
        <form
          className="d-flex flex-column flex-sm-row gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            openAddJob(url.trim());
            setUrl("");
          }}
        >
          <div className="input-group">
            <span className="input-group-text bg-white">
              <i className="bi bi-link-45deg" />
            </span>
            <input
              className="form-control"
              placeholder="Paste a job link…"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              aria-label="Job link"
            />
          </div>
          <button className="btn btn-primary text-nowrap" type="submit">
            <i className="bi bi-magic me-1" />
            {url.trim() ? "Detect details" : "Add job"}
          </button>
        </form>
        <div className="small text-muted mt-2">
          Don't have one yet? <Link to="/find">Search for jobs</Link>
        </div>
      </div>
    </div>
  );
}

function GettingStarted() {
  const { profile, documents, jobs, onboardingDismissed, dismissOnboarding, loadSampleData, toast } = useApp();
  const [loadingSample, setLoadingSample] = useState(false);
  const items = [
    { done: !!(profile.firstName && profile.email), label: "Fill in your profile", hint: "Used to autofill applications", to: "/profile" },
    { done: documents.resumes.length > 0, label: "Upload your resume", hint: "We tailor it for every job", to: "/documents" },
    { done: documents.templates.length > 0, label: "Add a cover letter template", hint: "We write letters from it", to: "/documents#templates" },
    { done: jobs.length > 0, label: "Add your first job", hint: "Paste a link or search", to: "/find" },
  ];
  const doneCount = items.filter((i) => i.done).length;
  if (onboardingDismissed || doneCount === items.length) return null;

  return (
    <div className="card mb-4">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h2 className="h6 mb-1">Get set up ({doneCount}/{items.length})</h2>
            <p className="text-muted small mb-0">A few minutes now makes every application faster.</p>
          </div>
          <button className="btn btn-sm btn-link text-muted" onClick={dismissOnboarding}>
            Dismiss
          </button>
        </div>
        <div className="progress mb-3" style={{ height: 6 }}>
          <div className="progress-bar" style={{ width: `${(doneCount / items.length) * 100}%` }} />
        </div>
        <div className="row g-2">
          {items.map((item) => (
            <div key={item.label} className="col-sm-6 col-xl-3">
              <Link to={item.to} className={`setup-item ${item.done ? "done" : ""}`}>
                <i className={`bi ${item.done ? "bi-check-circle-fill" : "bi-circle"}`} />
                <div>
                  <div className="small fw-medium">{item.label}</div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {item.hint}
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
        {jobs.length === 0 && (
          <div className="small text-muted mt-3">
            Just looking around?{" "}
            <button
              className="btn btn-link btn-sm p-0 align-baseline"
              disabled={loadingSample}
              onClick={async () => {
                setLoadingSample(true);
                if (await loadSampleData()) toast("Sample jobs loaded", "info");
                setLoadingSample(false);
              }}
            >
              {loadingSample ? "Loading…" : "Load sample jobs"}
            </button>{" "}
            to see how everything works.
          </div>
        )}
      </div>
    </div>
  );
}

function Dashboard() {
  const { jobs, profile, integrations } = useApp();
  const attention = attentionItems(jobs).slice(0, 6);
  const activity = jobs
    .flatMap((job) => (job.activity || []).map((a) => ({ ...a, job })))
    .sort((a, b) => new Date(b.at) - new Date(a.at))
    .slice(0, 6);

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            {greeting()}
            {profile.firstName ? `, ${profile.firstName}` : ""}
          </h1>
          <p className="page-subtitle">Here's where your job search stands today.</p>
        </div>
      </div>

      {integrations.configured?.ai && !integrations.access?.allowed && (
        <AccessNotice
          feature="AI help"
          message="Resume tailoring, cover letters, AI answers, Platypus and recruiter lookup cost money to run, so they're available by request. Everything else is free."
          className="mb-4"
        />
      )}
      <PasteLinkCard />
      <GettingStarted />

      <div className="stat-grid mb-4">
        {ACTIVE_STAGES.map((s) => (
          <Link key={s.id} to={`/tracker?stage=${s.id}`} className="stat-card" style={{ "--stage-color": s.color }}>
            <div className="d-flex justify-content-between align-items-center">
              <span className="small text-muted">{s.label}</span>
              <i className={`bi ${s.icon} stat-icon`} />
            </div>
            <div className="stat-value">{jobs.filter((j) => j.stage === s.id).length}</div>
            <div className="stat-hint">{s.description}</div>
          </Link>
        ))}
      </div>

      <div className="row g-4">
        <div className="col-lg-7">
          <div className="card h-100">
            <div className="card-header-lite">
              <h2 className="h6 mb-0">Needs your attention</h2>
              <span className="text-muted small">{attention.length} to do</span>
            </div>
            {attention.length === 0 ? (
              <EmptyState icon="bi-check2-all" title="You're all caught up" text="New to-dos show up here as your applications move along." />
            ) : (
              <div className="list-flush">
                {attention.map((item, i) => (
                  <Link key={i} to={item.to} className="attention-row">
                    <span className="attention-icon" style={{ "--c": item.color }}>
                      <i className={`bi ${item.icon}`} />
                    </span>
                    <div className="flex-grow-1 min-w-0">
                      <div className="fw-medium text-truncate">{item.text}</div>
                      <div className="small text-muted text-truncate">
                        {item.detailType === "date" ? formatDateTime(item.detail) : item.detail}
                      </div>
                    </div>
                    <span className="btn btn-sm btn-light flex-shrink-0">
                      {item.cta} <i className="bi bi-chevron-right small" />
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="col-lg-5 d-flex flex-column gap-4">
          <div className="card">
            <div className="card-header-lite">
              <h2 className="h6 mb-0">Pipeline</h2>
              <Link to="/tracker" className="small">
                Open tracker
              </Link>
            </div>
            <div className="card-body pt-0">
              <PipelineChart jobs={jobs} />
            </div>
          </div>
          <div className="card">
            <div className="card-header-lite">
              <h2 className="h6 mb-0">Applications per week</h2>
            </div>
            <div className="card-body pt-0">
              <WeeklyChart jobs={jobs} />
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="card">
            <div className="card-header-lite">
              <h2 className="h6 mb-0">Recent activity</h2>
            </div>
            {activity.length === 0 ? (
              <EmptyState icon="bi-clock-history" title="No activity yet" text="Add a job to get started." />
            ) : (
              <div className="list-flush">
                {activity.map((a) => (
                  <Link key={a.id} to={`/jobs/${a.job.id}`} className="activity-row">
                    <CompanyLogo name={a.job.company} size={28} />
                    <div className="flex-grow-1 min-w-0 small text-truncate">
                      <span className="fw-medium">{a.text}</span>
                      <span className="text-muted">
                        {" "}
                        · {a.job.title} at {a.job.company}
                      </span>
                    </div>
                    <span className="small text-muted flex-shrink-0">{timeAgo(a.at)}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default Dashboard;
